/**
 * `/save-card` route. Zero-auth tokenization flow (Adyen **Advanced**):
 *
 *   parse URL params (tokenId, country, type)
 *   → dispatch createTokenizeSession (clientKey/environment + bearer token)
 *   → render <PaymentForm mode="tokenize" .../>
 *   → on submit → dispatch authoriseCard (BFF /authorise relays the encrypted
 *     card to Adyen `/payments`, returning the EXACT recurringDetailReference)
 *   → resolve the Adyen callback with /authorise response and continue through
 *     onAdditionalDetails → dispatch authoriseCardDetails (BFF /authorise-details)
 *   → on `Authorised` → dispatch savePaymentMethod with the exact
 *     `adyenRecurringDetailReference` (Medusa flow (a) — deterministic match)
 *   → navigate to the status page mapped from the Adyen result code.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';

import PageShell from '../../components/common/PageShell';
import PaymentForm from '../../components/payment/PaymentForm';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { env } from '../../lib/env';
import { parseSaveCardParams } from '../../lib/urlParams';
import { ERROR_MESSAGES, UI_CONFIG } from '../../utils/constants';
import { useQuery } from '../../utils/hooks';
import { getRedirectUrl } from '../../utils/redirect';
import type {
  AdyenAdvancedActions,
  AdyenAuthoriseAdditionalData,
  AdyenAuthoriseSubmitData,
  AdyenPaymentCompletedResult,
} from '../../types/adyen';
import type { PaymentMethodAuthoriseResponse } from '../../types/api';
import type { SaveCardRouteParams } from '../../types/routes';

import { loadCart, resetCheckout } from '../checkout/checkoutSlice';
import {
  authoriseCard,
  authoriseCardDetails,
  createTokenizeSession,
  resetTokenize,
  savePaymentMethod,
} from './tokenizeSlice';

/**
 * Lightweight breadcrumb logger for the save-card flow. Centralises the
 * `no-console` suppression and prefixes every line (`[save-card]`) so the
 * end-to-end tokenize → 3DS → save path is greppable in the browser console.
 * PCI-safe by contract: callers MUST pass only non-sensitive fields
 * (resultCode, booleans, ids) — NEVER encrypted card blobs, CVC or holder name.
 */
const trace = (event: string, detail?: Record<string, unknown>): void => {
  // eslint-disable-next-line no-console
  console.info(`[save-card] ${event}`, detail ?? '');
};

const SAVE_CARD_CONTEXT_KEY = 'fpa-save-card-context';

function readStorage(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key) ?? window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Ignore sessionStorage write failures.
  }
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Ignore localStorage write failures.
  }
}

function removeStorage(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Ignore sessionStorage clear failures.
  }
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore localStorage clear failures.
  }
}

interface StoredSaveCardContext extends SaveCardRouteParams {
  tokenizeSessionToken?: string;
  holderName?: string;
  encryptedSecurityCode?: string;
}

function readStoredSaveCardContext(): StoredSaveCardContext | null {
  try {
    const raw = readStorage(SAVE_CARD_CONTEXT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredSaveCardContext>;
    if (
      typeof parsed.tokenId !== 'string' ||
      typeof parsed.country !== 'string' ||
      typeof parsed.type !== 'string'
    ) {
      return null;
    }
    if (parsed.type !== 'onetime' && parsed.type !== 'recurring') {
      return null;
    }
    return {
      tokenId: parsed.tokenId,
      country: parsed.country,
      type: parsed.type,
      ...(typeof parsed.tokenizeSessionToken === 'string'
        ? { tokenizeSessionToken: parsed.tokenizeSessionToken }
        : {}),
      ...(typeof parsed.holderName === 'string' ? { holderName: parsed.holderName } : {}),
      ...(typeof parsed.encryptedSecurityCode === 'string'
        ? { encryptedSecurityCode: parsed.encryptedSecurityCode }
        : {}),
    };
  } catch {
    return null;
  }
}

function writeStoredSaveCardContext(
  params: SaveCardRouteParams,
  extras?: {
    tokenizeSessionToken?: string;
    holderName?: string;
    encryptedSecurityCode?: string;
  },
): void {
  try {
    const payload = JSON.stringify({
      tokenId: params.tokenId,
      country: params.country,
      type: params.type,
      ...(extras?.tokenizeSessionToken
        ? { tokenizeSessionToken: extras.tokenizeSessionToken }
        : {}),
      ...(extras?.holderName ? { holderName: extras.holderName } : {}),
      ...(extras?.encryptedSecurityCode
        ? { encryptedSecurityCode: extras.encryptedSecurityCode }
        : {}),
    } satisfies StoredSaveCardContext);
    writeStorage(SAVE_CARD_CONTEXT_KEY, payload);
  } catch {
    // Ignore storage write failures — the in-memory inline flow still works.
  }
}

function clearStoredSaveCardContext(): void {
  removeStorage(SAVE_CARD_CONTEXT_KEY);
}

function buildRedirectDetails(query: URLSearchParams): AdyenAuthoriseAdditionalData | null {
  const redirectResult = query.get('redirectResult')?.trim() ?? '';
  const threeDSResult = query.get('threeDSResult')?.trim() ?? '';
  const payload = query.get('payload')?.trim() ?? '';
  const details: Record<string, string> = {};

  if (redirectResult) details.redirectResult = redirectResult;
  if (threeDSResult) details.threeDSResult = threeDSResult;
  if (payload) details.payload = payload;

  return Object.keys(details).length > 0 ? { details } : null;
}

const SaveCardPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const query = useQuery();
  const redirectDetails = useMemo(() => buildRedirectDetails(query), [query]);
  const storedContext = useMemo(
    () => (redirectDetails ? readStoredSaveCardContext() : null),
    [redirectDetails],
  );
  const params = parseSaveCardParams(query) ?? storedContext;
  const tokenId = params?.tokenId;
  const country = params?.country;
  const type = params?.type;
  const { session, loading, error } = useAppSelector((state) => state.tokenize);
  // The cart is loaded so we can (a) block save-card when it is empty and
  // (b) show the subscription footnote when any line item is a subscription.
  const cart = useAppSelector((state) => state.checkout.cart);
  const cartLoading = useAppSelector((state) => state.checkout.loading);
  const cartError = useAppSelector((state) => state.checkout.error);
  const [isSubmitInFlight, setIsSubmitInFlight] = useState(false);
  // Bumped when the shopper CANCELS the 3DS2 challenge, to force a fresh
  // PaymentForm mount. The encrypted card fields are single-use, so the card
  // must be re-entered — remounting gives them a clean form to retry with
  // instead of landing on the failed status page.
  const [retryNonce, setRetryNonce] = useState(0);
  // Human-readable notice shown above the (re-rendered) card form when the
  // previous attempt didn't go through — e.g. an incorrect 3DS passcode/
  // cancelled challenge, or a hard Adyen SDK error. Cleared as soon as the
  // shopper submits a new attempt (see `onAuthoriseSubmit`).
  const [formNotice, setFormNotice] = useState<string | null>(null);

  // Card-form extras captured at submit time, needed AFTER a 3DS round-trip
  // (holder name for the save body; encrypted CVC forwarded to `/order`).
  const submitExtrasRef = useRef<{ holderName?: string; encryptedSecurityCode?: string }>({});
  const redirectHandledRef = useRef(false);
  const initKeyRef = useRef<string | null>(null);
  const activeTokenizeSessionToken =
    session?.tokenizeSessionToken ?? storedContext?.tokenizeSessionToken ?? null;

  // Keep the page overlay loader for bootstrap only (session/cart init).
  // During card submit / 3DS handoff, PaymentForm now handles visibility and
  // we avoid showing a second global loader layer.
  const shellLoading = cartLoading || (!activeTokenizeSessionToken && loading) || isSubmitInFlight;

  useEffect(() => {
    if (!storedContext) return;
    submitExtrasRef.current = {
      ...(storedContext.holderName ? { holderName: storedContext.holderName } : {}),
      ...(storedContext.encryptedSecurityCode
        ? { encryptedSecurityCode: storedContext.encryptedSecurityCode }
        : {}),
    };
  }, [storedContext]);

  useEffect(() => {
    if (!tokenId || !country || !type) return;
    writeStoredSaveCardContext({ tokenId, country, type }, {
      tokenizeSessionToken: activeTokenizeSessionToken ?? undefined,
      ...submitExtrasRef.current,
    });
  }, [
    tokenId,
    country,
    type,
    activeTokenizeSessionToken,
  ]);

  useEffect(() => {
    if (!tokenId || !country || !type) return;
    const initKey = `${tokenId}:${country}:${type}`;
    if (initKeyRef.current === initKey) return;
    initKeyRef.current = initKey;

    const shouldCreateSession = !(redirectDetails && storedContext?.tokenizeSessionToken);
    if (shouldCreateSession && !activeTokenizeSessionToken) {
      void dispatch(
        createTokenizeSession({
          tokenId,
          countryCode: country,
        }),
      )
        .unwrap()
        .then((created) => {
          writeStoredSaveCardContext({ tokenId, country, type }, {
            tokenizeSessionToken: created.tokenizeSessionToken,
            ...submitExtrasRef.current,
          });
          trace('tokenize-session:created');
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('[save-card] tokenize-session:error', err);
        });
    } else {
      trace('tokenize-session:reused-from-storage');
    }

    void dispatch(
      loadCart({
        tokenId,
        countryCode: country,
      }),
    );
  }, [
    dispatch,
    tokenId,
    country,
    type,
    redirectDetails,
    storedContext?.tokenizeSessionToken,
    activeTokenizeSessionToken,
  ]);

  useEffect(() => {
    return () => {
      dispatch(resetTokenize());
      dispatch(resetCheckout());
    };
  }, [dispatch]);

  // Persist the vaulted card and navigate to the success status page. The
  // Advanced flow gives us the EXACT `recurringDetailReference`, so we pass it
  // straight through — Medusa flow (a) matches it against the shopper's stored
  // methods deterministically (no newest-method heuristic, no re-entry ambiguity).
  const saveAndNavigate = useCallback(
    (recurringDetailReference?: string): void => {
      if (!activeTokenizeSessionToken) return;
      const { holderName, encryptedSecurityCode } = submitExtrasRef.current;
      void dispatch(
        savePaymentMethod({
          tokenizeSessionToken: activeTokenizeSessionToken,
          body: {
            ...(recurringDetailReference
              ? { adyenRecurringDetailReference: recurringDetailReference }
              : {}),
            ...(holderName ? { holderName } : {}),
          },
        }),
      )
        .unwrap()
        .then((saved) => {
          clearStoredSaveCardContext();
          trace('save:success', { paymentMethodId: saved.paymentMethod.id });
          const qs = new URLSearchParams({
            context: 'save-card',
            paymentMethodId: saved.paymentMethod.id,
          });
          if (encryptedSecurityCode) {
            qs.set('encryptedSecurityCode', encryptedSecurityCode);
          }
          navigate(`/status/success?${qs.toString()}`);
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('[save-card] save:error', err);
          navigate('/status/error');
        });
    },
    [activeTokenizeSessionToken, dispatch, navigate],
  );

  // Branch on the Adyen result shared by `/authorise` and `/authorise-details`.
  const finaliseAuthorise = useCallback(
    (result: PaymentMethodAuthoriseResponse): void => {
      trace('authorise:result', {
        resultCode: result.resultCode,
        hasAction: Boolean(result.action),
        hasRecurringRef: Boolean(result.recurringDetailReference),
      });
      // When result still contains an action we expect Adyen to continue through
      // callback resolution in the current submit/details lifecycle.
      if (result.action) {
        trace('authorise:3ds-action');
        return;
      }
      if (result.resultCode === 'Authorised') {
        // Some Authorised responses omit recurringDetailReference. In that
        // case, backend /payment-methods/save resolves the token via the
        // tokenize-session snapshot diff for this shopper.
        saveAndNavigate(result.recurringDetailReference);
        return;
      }
      // Refused / Cancelled / Error / Authorised-without-ref — nothing to persist.
      clearStoredSaveCardContext();
      const redirect = getRedirectUrl(
        result.resultCode as AdyenPaymentCompletedResult['resultCode'],
      );
      navigate(redirect);
    },
    [navigate, saveAndNavigate],
  );

  useEffect(() => {
    if (!activeTokenizeSessionToken || !redirectDetails) return;
    if (redirectHandledRef.current) return;
    redirectHandledRef.current = true;
    trace('authorise-details:resume', { detailKeys: Object.keys(redirectDetails.details) });
    void dispatch(
      authoriseCardDetails({
        tokenizeSessionToken: activeTokenizeSessionToken,
        body: {
          details: redirectDetails.details,
          ...(redirectDetails.paymentData ? { paymentData: redirectDetails.paymentData } : {}),
        },
      }),
    )
      .unwrap()
      .then(finaliseAuthorise)
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[save-card] authorise-details:resume-error', err);
        clearStoredSaveCardContext();
        navigate('/status/error');
      });
  }, [activeTokenizeSessionToken, dispatch, finaliseAuthorise, navigate, redirectDetails]);

  if (!params) {
    return (
      <PageShell>
        <div role="alert">Invalid save-card link — missing required parameters.</div>
      </PageShell>
    );
  }

  if (error || cartError) {
    const shown = error ?? cartError;
    return (
      <PageShell>
        <div role="alert">
          {shown?.code}: {shown?.message}
        </div>
      </PageShell>
    );
  }

  // Require at least one line item before allowing a card to be saved.
  if (cart && cart.items.length === 0) {
    return (
      <PageShell>
        <div role="alert">Your cart is empty — add at least one item before saving a card.</div>
      </PageShell>
    );
  }

  const hasSubscriptionItem =
    cart?.items.some((item) => item.purchaseType === 'subscription') ?? false;

  const onAuthoriseSubmit = (data: AdyenAuthoriseSubmitData, actions: AdyenAdvancedActions): void => {
    if (!activeTokenizeSessionToken) return;
    // Clear any notice left over from a previous failed/cancelled attempt —
    // the shopper is trying again with freshly re-entered card details.
    setFormNotice(null);
    const pm = data.paymentMethod;
    if (!pm.encryptedCardNumber || !pm.encryptedExpiryMonth || !pm.encryptedExpiryYear) {
      // eslint-disable-next-line no-console
      console.error('[save-card] authorise:submit missing encrypted card fields');
      actions.reject();
      navigate('/status/error');
      return;
    }
    trace('authorise:submit');
    submitExtrasRef.current = {
      ...(pm.holderName ? { holderName: pm.holderName } : {}),
      ...(pm.encryptedSecurityCode ? { encryptedSecurityCode: pm.encryptedSecurityCode } : {}),
    };
    writeStoredSaveCardContext(params, {
      tokenizeSessionToken: activeTokenizeSessionToken,
      ...submitExtrasRef.current,
    });
    void dispatch(
      authoriseCard({
        tokenizeSessionToken: activeTokenizeSessionToken,
        body: {
          encryptedCardNumber: pm.encryptedCardNumber,
          encryptedExpiryMonth: pm.encryptedExpiryMonth,
          encryptedExpiryYear: pm.encryptedExpiryYear,
          ...(pm.encryptedSecurityCode
            ? { encryptedSecurityCode: pm.encryptedSecurityCode }
            : {}),
          ...(pm.holderName ? { holderName: pm.holderName } : {}),
          browserInfo: { userAgent: navigator.userAgent, ...(data.browserInfo ?? {}) },
          origin: window.location.origin,
        },
      }),
    )
      .unwrap()
      .then((result) => {
        actions.resolve({
          resultCode: result.resultCode,
          ...(result.action ? { action: result.action } : {}),
        });
        if (!result.action) {
          finaliseAuthorise(result);
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[save-card] authorise:error', err);
        actions.reject();
        navigate('/status/error');
      });
  };

  const onAuthoriseAdditionalDetails = (
    data: AdyenAuthoriseAdditionalData,
    actions: AdyenAdvancedActions,
  ): void => {
    if (!activeTokenizeSessionToken) return;
    trace('authorise-details:submit', { detailKeys: Object.keys(data.details ?? {}) });
    void dispatch(
      authoriseCardDetails({
        tokenizeSessionToken: activeTokenizeSessionToken,
        body: {
          details: data.details,
          ...(data.paymentData ? { paymentData: data.paymentData } : {}),
        },
      }),
    )
      .unwrap()
      .then((result) => {
        actions.resolve({
          resultCode: result.resultCode,
          ...(result.action ? { action: result.action } : {}),
        });
        if (result.action) return;
        // 3DS2 flow only: if 3DS authentication was cancelled or not completed,
        // re-show the card form for another attempt instead of routing to
        // /status/failed. Note the Adyen test simulator reports a shopper
        // cancel as resultCode 'Refused' + refusalReason '3D Not Authenticated',
        // so we match that here. Genuine hard declines (other refusal reasons)
        // still fall through to the normal finalise -> failed path below.
        const threeDSNotCompleted =
          result.resultCode === 'Cancelled' ||
          (result.resultCode === 'Refused' &&
            /3d not authenticated|cancel/i.test(result.refusalReason ?? ''));
        if (threeDSNotCompleted) {
          trace('authorise-details:3ds-not-completed-retry', {
            resultCode: result.resultCode,
            refusalReason: result.refusalReason,
          });
          setFormNotice(ERROR_MESSAGES.THREE_DS_NOT_COMPLETED);
          setIsSubmitInFlight(false);
          setRetryNonce((n) => n + 1);
          return;
        }
        finaliseAuthorise(result);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[save-card] authorise-details:error', err);
        actions.reject();
        navigate('/status/error');
      });
  };

  return (
    <PageShell loading={shellLoading}>
      {activeTokenizeSessionToken && cart && cart.items.length > 0 && (
        // `.payment-container` (App.css) is `display: flex` with NO
        // `flex-direction` set (defaults to row). It previously only ever had
        // ONE child (`PaymentForm`), so that never mattered. Now that we can
        // render a notice ABOVE the form, both must be wrapped in a single
        // full-width column container — otherwise they render as side-by-side
        // flex siblings instead of stacked.
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
          {formNotice && (
            <Box
              role="alert"
              sx={{
                color: UI_CONFIG.ERROR_COLOR,
                mb: 2,
                px: '20px',
              }}
            >
              {formNotice}
            </Box>
          )}
          <PaymentForm
            key={`paymentform-${retryNonce}`}
            clientKey={env.adyenClientKey}
            environment={env.adyenEnvironment}
            country={params.country}
            mode="tokenize"
            hasSubscriptionItem={hasSubscriptionItem}
            onSubmitInFlightChange={setIsSubmitInFlight}
            onAuthoriseSubmit={onAuthoriseSubmit}
            onAuthoriseAdditionalDetails={onAuthoriseAdditionalDetails}
            onError={(err) => {
              // eslint-disable-next-line no-console
              console.error('[save-card] Adyen error', err);
              // Hard Adyen SDK error (iframe load/network failure). Previously
              // this only logged: isSubmitInFlight/hideShopperInputs stayed
              // true forever with no way for the shopper to retry. Unstick the
              // form the same way the 3DS-not-completed path does — remount
              // with a fresh PaymentForm and surface a notice.
              setFormNotice(ERROR_MESSAGES.PAYMENT_FORM_ERROR);
              setIsSubmitInFlight(false);
              setRetryNonce((n) => n + 1);
            }}
          />
        </Box>
      )}
    </PageShell>
  );
};

export default SaveCardPage;
